import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isWebsiteRoute, websiteTabsFor } from './WebsiteNav';
import { getRegistrations } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

interface SidebarProps {
  onLogout: () => void;
}

function Sidebar({ onLogout }: SidebarProps) {
  const { clubName, email, plan, refreshPlan, features } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const displayName = clubName || 'Admin';
  const displayEmail = email || '';
  const isFree = plan === 'free';
  // Players visibility is handled inside the Club Management module
  // (TeamNav gates the Players tab on features.players); the sidebar
  // just links to the module entry point.
  // Email is a Club + Pro feature. Plan-gated only (matches the lambda's
  // ELIGIBLE_PLANS set in lambda/routes/email.ts).
  const emailListEnabled = plan === 'club' || plan === 'pro';
  // Collections (membership/fee collection), staged per-club flag.
  const collectionsEnabled = features?.collections === true;
  // Fixtures & results, dark-launch pilot, per-club flag.
  const fixturesEnabled = features?.fixtures === true;
  const registrationEnabled = features?.registration === true;

  // Unread-style count on Registrations. Only 'verified' rows count, those are
  // the ones a human has to decide on. Pending rows are unconfirmed and the API
  // withholds them anyway, so badging them would send clubs to an empty screen.
  const [pendingRegs, setPendingRegs] = useState(0);
  useEffect(() => {
    if (!registrationEnabled) return;
    let alive = true;
    const refresh = () => getRegistrations()
      .then(rows => { if (alive) setPendingRegs(rows.filter(r => r.status === 'verified').length); })
      .catch(() => {});
    refresh();
    // Re-count when the Registrations page acts, and on a slow poll so a badge
    // does not sit stale while an admin leaves the tab open all evening.
    window.addEventListener('registrations-changed', refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      alive = false;
      window.removeEventListener('registrations-changed', refresh);
      window.clearInterval(timer);
    };
  }, [registrationEnabled]);
  // Custom Domain (BYOD), operator-set feature flag only. We DON'T gate
  // on plan any more: Pro gets BYOD included, Club/Free can subscribe as
  // a paid add-on (£3/mo or £5/mo + £15 setup), so every plan that has
  // the flag on needs to see the entry to either manage their sub or
  // see the upsell CTA. Server still 402s anyone without a valid path.

  // Refresh plan from server on mount (in case user upgraded via Stripe)
  useEffect(() => {
    refreshPlan();
  }, []);

  // Close sidebar when route changes (mobile navigation)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        className="hamburger-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        <span className={`hamburger-line ${isOpen ? 'open' : ''}`} />
        <span className={`hamburger-line ${isOpen ? 'open' : ''}`} />
        <span className={`hamburger-line ${isOpen ? 'open' : ''}`} />
      </button>

      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h1 className="sidebar-title">{clubName || 'AllGrassroots'}</h1>
        <p className="sidebar-subtitle">Admin Dashboard</p>
      </div>

      <div className="sidebar-user">
        <div className="user-avatar">
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div className="user-info">
          <p className="user-name">{displayName}</p>
          <p className="user-email">{displayEmail}</p>
        </div>
      </div>

      <div className="sidebar-plan-badge">
        <span className={`plan-badge ${plan}`}>
          {plan === 'pro' ? 'Pro Plan' : plan === 'club' ? 'Club Plan' : 'Free Plan'}
        </span>
      </div>

      <nav className="sidebar-nav">
        <p className="nav-group-title">Overview</p>
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/icons/dashboard.png" alt="" aria-hidden="true" />
          <span className="nav-text">Dashboard</span>
        </NavLink>

        

        <p className="nav-group-title">People &amp; money</p>
        {registrationEnabled && (
          <NavLink
            to="/registrations"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/icons/registrations.png" alt="" aria-hidden="true" />
            <span className="nav-text">Registrations</span>
            {pendingRegs > 0 && (
              <span className="nav-badge" aria-label={`${pendingRegs} waiting for a decision`}>
                {pendingRegs > 99 ? '99+' : pendingRegs}
              </span>
            )}
          </NavLink>
        )}

        
        <NavLink
          to="/teams"
          className={`nav-item ${['/teams', '/players', '/coaches', '/members', '/guardians'].includes(location.pathname) ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/icons/clubmngt.png" alt="" aria-hidden="true" />
          <span className="nav-text">Club Management</span>
        </NavLink>

        
        {collectionsEnabled && (
          <NavLink
            to="/collections"
            className={`nav-item ${location.pathname.startsWith('/collections') ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/icons/collections.png" alt="" aria-hidden="true" />
            <span className="nav-text">Collections</span>
          </NavLink>
        )}

        <p className="nav-group-title">The season</p>
        {fixturesEnabled && (
          <NavLink
            to="/fixtures"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/icons/fixtures.png" alt="" aria-hidden="true" />
            <span className="nav-text">Fixtures</span>
          </NavLink>
        )}

        

        <p className="nav-group-title">Your website</p>
        <NavLink
          to="/content"
          className={`nav-item ${isWebsiteRoute(location.pathname) ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/icons/website.png" alt="" aria-hidden="true" />
          <span className="nav-text">Website</span>
        </NavLink>

        {/* The nine pages, as small text, while you are in the section. Not
            shown from elsewhere in the admin, expanding permanently would put
            the sidebar back at 21 entries, which is what this replaced. Hidden
            on mobile by CSS, where the tab strip takes over instead. */}
        {isWebsiteRoute(location.pathname) && (
          <div className="nav-sub">
            {websiteTabsFor(plan).map(t => (
              <NavLink
                key={t.to}
                to={t.gated ? '/upgrade' : t.to}
                className={({ isActive }) =>
                  `nav-sub-item ${t.gated ? 'gated' : ''} ${isActive && !t.gated ? 'active' : ''}`
                }
              >
                {t.label}
                {t.gated && <span className="upgrade-badge">Upgrade</span>}
              </NavLink>
            ))}
          </div>
        )}

        <p className="nav-group-title">Reach out</p>
        {emailListEnabled && (
          <NavLink
            to="/email/compose"
            className={({ isActive }) =>
              `nav-item ${isActive || location.pathname.startsWith('/email/') ? 'active' : ''}`
            }
          >
            <img className="nav-icon" src="/icons/email.png" alt="" aria-hidden="true" />
            <span className="nav-text">Send Email</span>
          </NavLink>
        )}

        

        <p className="nav-group-title">Account</p>
        {/* One or the other, never both, a free club is asked to upgrade,
            a paying club gets its billing. */}
        {isFree ? (
          <NavLink
            to="/upgrade"
            className={({ isActive }) => `nav-item nav-item-upgrade ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/icons/upgrade.png" alt="" aria-hidden="true" />
            <span className="nav-text">Upgrade</span>
          </NavLink>
        ) : (
          <NavLink
            to="/upgrade"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/icons/billing.png" alt="" aria-hidden="true" />
            <span className="nav-text">Billing</span>
          </NavLink>
        )}

        
        {/* Shown to every club, linking to the real page: the domain is an
            add-on any plan can buy and that page carries its own pricing and
            Subscribe button, so it is the upgrade path. */}
        <NavLink
          to="/custom-domain"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/icons/owndomain.png" alt="" aria-hidden="true" />
          <span className="nav-text">Custom Domain</span>
        </NavLink>

        
        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/icons/settings.png" alt="" aria-hidden="true" />
          <span className="nav-text">Settings</span>
        </NavLink>
      


</nav>

      <div className="sidebar-footer">
        <button onClick={onLogout} className="logout-btn">
          <img className="nav-icon" src="/icons/logout.png" alt="" aria-hidden="true" />
          <span className="nav-text">Logout</span>
        </button>
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
